import React, { useState, useEffect, useRef, useContext } from "react";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Button } from "@progress/kendo-react-buttons";
import { FiAlignRight } from "react-icons/fi";
import { Popup } from "@progress/kendo-react-popup";
import { setQuery, updateArea } from "../../redux/querySlice";
import { useSelector, useDispatch } from "react-redux";
import Clause from "./Clause";
import "./QueryFilter.css";
import { getMapLayerById } from "../utils/mapUtils";
import MapContext from "../MapContext";
import { Style, Stroke } from "ol/style";
import { executeQuery } from "./queryUtils";
import { setResultsData } from "../../redux/resultsSlice";
import { filterReadFields, filterReadLayers, makeArray } from "../../permissionUtils";

const QueryFilter = () => {
    const dispatch = useDispatch();
    const { map } = useContext(MapContext);

    // Permissions
    const authPermissions = useSelector(
        (state) => state.authorizedPermissions.fieldPermissions
    );

    const layerPermissions = useSelector(
        (state) => state.authorizedPermissions.layerPermissions
    );

    // Global state
    const data = useSelector((state) => state.configData.vectorLayerProperties);
    // Filter layers with permissions
    const permissedData = filterReadLayers(data, layerPermissions);
    //    Queryable vector layers
    const queryData = permissedData.filter((item) => item.Queryable == true);
    const fullQuery = useSelector((state) => state.query);
    // Local state
    const [dropDownArray, setDropDownArray] = useState(undefined);
    const [dataSource, setDataSource] = useState();
    // Popup utils
    const [showPopup, setShowPopup] = useState(false);
    const popupAnchor = useRef();
    const popupRef = useRef();

    const firstRenderRef = useRef(true);

    // UI feedback
    const [noResults, setNoResults] = useState(false);

    // Names of data sources (roads, parcels, etc...)
    const dataSources = queryData.map((d) => d.DisplayName);

    // Load initial query (data source is a local variable in this useEffect)
    useEffect(() => {
        if (fullQuery.query === undefined) {
            const dataSource = queryData[0];
            if (!dataSource) return;
            const fields = parseDropDownData(dataSource, authPermissions);
            setDropDownArray(fields);
            const query = {
                dataSource: dataSource.DisplayName,
                mapArea: "All",
                query: {
                    logicOperator: "AND",
                    conditions: [
                        {
                            field: fields[0],
                            operator: stringOperators[0],
                            value: "",
                        },
                    ],
                },
            };
            dispatch(setQuery(query));
        } else {
            const sourceName = fullQuery.dataSource;
            const dataSource = queryData.find((d) => d.DisplayName === sourceName);
            const fields = parseDropDownData(dataSource, authPermissions);
            setDropDownArray(fields);
        }
    }, []);

    // Update when data source changes (data source is state in this useEffect)
    useEffect(() => {
        if (firstRenderRef.current === true) return;
        const sourceData = queryData.find((d) => d.DisplayName === dataSource);
        const fields = parseDropDownData(sourceData, authPermissions);
        setDropDownArray(fields);
        const query = {
            dataSource: dataSource,
            mapArea: "All",
            query: {
                logicOperator: "AND",
                conditions: [
                    {
                        field: fields[0],
                        operator: stringOperators[0],
                        value: "",
                    },
                ],
            },
        };
        dispatch(setQuery(query));
    }, [dataSource]);

    const handleSetDataSource = (e) => {
        setDataSource(e.value);
    };

    const handlePopup = () => {
        setShowPopup(!showPopup);
    };

    const handleQuerySave = () => {
        window.localStorage.setItem("query", JSON.stringify(fullQuery));
    };

    const handleLoadQuery = () => {
        dispatch(setQuery(testQuery));
        const sourceData = queryData.find((d) => d.DisplayName === testQuery.dataSource);
        let ddData = parseDropDownData(sourceData, authPermissions);
        setDropDownArray(ddData);
    };

    const handleMakeQuery = () => {
        const dataSet = queryData.find((d) => d.DisplayName === fullQuery.dataSource);
        const layer = getMapLayerById(map, dataSet.Id);
        const featureIds = [];

        if (fullQuery.mapArea === "Current Extent") {
            const extent = map.getView().calculateExtent(map.getSize());
            const features = layer.getSource().getFeaturesInExtent(extent);
            features.forEach((f) => {
                if (
                    executeQuery(
                        f.values_,
                        fullQuery.query,
                        queryData,
                        fullQuery.dataSource
                    )
                ) {
                    featureIds.push(f.id_);
                }
            });
        } else if (fullQuery.mapArea === "All") {
            const features = layer.getSource().getFeatures();
            features.forEach((f) => {
                if (
                    executeQuery(
                        f.values_,
                        fullQuery.query,
                        queryData,
                        fullQuery.dataSource
                    )
                ) {
                    featureIds.push(f.id_);
                }
            });
        }
        const results = {
            DisplayName: dataSet.DisplayName,
            FeatureLabel: dataSet.FeatureLabel,
            FeatureDescription: dataSet.FeatureDescription,
            Id: dataSet.Id,
            Features: [...featureIds],
        };
        if (results.Features.length === 0) {
            // handle no results
            setNoResults(true);
            setTimeout(() => {
                setNoResults(false);
            }, 4000);
        } else {
            dispatch(setResultsData([results]));
        }
    };

    const handleAreaSelection = (e) => {
        const newArea = e.value;
        dispatch(updateArea(newArea));
    };

    // Popup
    const detectClick = (e) => {
        if (
            !popupRef.current.contains(e.target) &&
            !popupAnchor.current.contains(e.target)
        ) {
            setShowPopup(false);
        }
    };
    // Popup
    useEffect(() => {
        if (showPopup) {
            document.addEventListener("mousedown", detectClick);
        }
        return () => {
            document.removeEventListener("mousedown", detectClick);
        };
    }, [showPopup]);

    // track first render
    useEffect(() => {
        if (firstRenderRef.current === true) firstRenderRef.current = false;
    }, []);

    return (
        <>
            {fullQuery.query !== undefined ? (
                <div className="query-content" id="query">
                    {/* <div
                        style={{ width: "20px" }}
                        onClick={handlePopup}
                        ref={popupAnchor}
                        className="layer-options-button">
                        <Popup anchor={popupAnchor.current} show={showPopup}>
                            <div ref={popupRef}>
                                <div className="query-option" onClick={handleQuerySave}>
                                    Save Query
                                </div>
                                <div className="query-option" onClick={handleLoadQuery}>
                                    Load Query
                                </div>
                            </div>
                        </Popup>{" "}
                        <FiAlignRight style={{ float: "right" }} />
                    </div> */}
                    <h3 style={{ textAlign: "center" }}>Query</h3>
                    <div>
                        <DropDownList
                            data={dataSources}
                            label="Data Source"
                            value={fullQuery.dataSource}
                            onChange={handleSetDataSource}
                            style={{ fontSize: "14px" }}
                            className="query-dropdown"
                        />
                        <DropDownList
                            data={["All", "Current Extent"]}
                            label="Map Area"
                            onChange={handleAreaSelection}
                            value={fullQuery.mapArea}
                            style={{ fontSize: "14px" }}
                            className="query-dropdown"
                        />
                    </div>
                    <Clause
                        dropDownArray={dropDownArray}
                        dataSource={dataSource}
                        query={fullQuery.query}
                        index={["base"]}
                        filteredData={queryData}
                    />
                    <div className="query-actions">
                        <Button onClick={handleMakeQuery}>Query</Button>
                    </div>
                    <br />
                    {noResults && <div>No results found for query</div>}
                </div>
            ) : (
                <div style={{ left: "50%" }}>
                    <i>No queryable data</i>
                </div>
            )}
        </>
    );
};

export default QueryFilter;

/**
 * Takes in layer and permissions and outputs drop down options
 * @param {object} selectedData Layer attributes from state
 * @param {object[]} permissions Permissions for each field
 * @returns {string[] | []} Data to give to drop down when selecting field to query
 */
const parseDropDownData = (selectedData, permissions) => {
    let dropDownData;
    // Check for any falsy values
    // An empty <Fields /> will generate empty string
    if (!selectedData.Fields) {
        dropDownData = [{ Name: `${selectedData.DisplayName}` }];
    } else {
        dropDownData = makeArray(selectedData.Fields.Field).filter(
            (d) => d.Queryable !== false
        );
        dropDownData = filterReadFields(dropDownData, permissions, selectedData.Id);
    }
    let ddData;
    if (dropDownData[0]?.DisplayIndex !== undefined) {
        ddData = dropDownData
            .map((d) => ({
                DisplayName: d.DisplayName ?? d.Name,
                i: d.DisplayIndex,
            }))
            .sort((a, b) => (a.i > b.i ? 1 : -1));
        ddData = ddData.map((d) => d.DisplayName);
    } else {
        ddData = dropDownData.map((d) => d.DisplayName ?? d.Name);
    }
    return ddData;
};

const stringOperators = [
    "=",
    "!=",
    "contains",
    "does not contain",
    "starts with",
    "ends with",
];

/**
 * @typedef {Object} QueryContainer
 * @property {string} dataSource DisplayName of layer being queired
 * @property {string} mapArea Area to query,
 * @property {Query} query Query to run on a feature
 */

/**
 * @typedef {Object} Query
 * @property {string} logicOperator AND or OR SQL operator
 * @property {Condition[]} conditions
 */

/**
 * @typedef {Query | Clause} Condition
 */

/**
 * @typedef {Object} Clause
 * @property {string} field Name of feature field to query
 * @property {string} operator SQL logic operator depended on field datatype
 * @property {string | number} value
 */

/** @type {QueryContainer} */
const testQuery = {
    dataSource: "Parcel",
    mapArea: "Current Extent",
    query: {
        logicOperator: "OR",
        conditions: [
            {
                field: "Name",
                operator: "contains",
                value: "ALLEN",
            },
            {
                field: "Name",
                operator: "contains",
                value: "TURNBULL",
            },
            {
                logicOperator: "AND",
                conditions: [
                    {
                        field: "GIS Acres",
                        operator: ">",
                        value: 5,
                    },
                    {
                        field: "Deed Date",
                        operator: "<",
                        value: 1364251675000,
                    },
                ],
            },
        ],
    },
};
